import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Paths } from "@/paths";
import {
    Breadcrumb, Button, Card, EmptyState, IdChip, PageHeader, ReadOnlyTag, SectionLabel, SegmentedControl, Tabs,
} from "@/shared/components/ds";
import { OpLaunchPage } from "../OpLaunchPage";
import pageStyles from "../opLaunchPages.module.css";
import { DashboardFilterBar } from "./DashboardFilterBar";
import { filtersFromSearch, formatDay, searchFromFilters } from "./dashboardFilters";
import styles from "./dashboard.module.css";
import { lastDay, SummaryStrip } from "./SummaryStrip";
import { TileWidget } from "./TileWidget";
import { TILES } from "./tiles";
import { useLaunchMain } from "./useLaunchMain";
import { useLaunchPool } from "./useLaunchPool";

const SECTIONS = [
    { value: "overview", label: "Overview" },
    { value: "impact", label: "Impact and Conversions" },
    { value: "spend", label: "Spend and Impressions" },
    { value: "notes", label: "Notes and Key Dates" },
];

const NOTE_ORDERS = ["Newest first", "Oldest first"];
const NOTE_SORT: Record<string, string> = { "Newest first": "-datetime_posted", "Oldest first": "datetime_posted" };

/** The nearest ancestor that scrolls: the App's <main>, or nothing (the window) in a test. */
function scrollParent(el: HTMLElement | null): HTMLElement | null {
    let node = el?.parentElement ?? null;
    while (node) {
        const overflow = getComputedStyle(node).overflowY;
        if (overflow === "auto" || overflow === "scroll") return node;
        node = node.parentElement;
    }
    return null;
}

const TAB_OFFSET = 62;

/**
 * The OP Launch Dashboard (MPN-2230), read-only from the frozen OP warehouse. The URL carries
 * the launch and every filter. Nothing loads until the URL names a launch (MPN-2313): the
 * page opens on the picker, and a link from OP Launches opens on its launch. Date Range
 * scopes the widgets; the other filters narrow the picker. The tabs are wayfinding down one
 * long page, not separate views.
 */
export default function OpLaunchDashboardPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const filters = filtersFromSearch(searchParams);
    const apply = (next: typeof filters) => setSearchParams(searchFromFilters(next));
    const { launches, loading: launchesLoading } = useLaunchPool(filters);
    const main = useLaunchMain(filters.launch_id);
    const [openRequest, setOpenRequest] = useState(0);
    const [noteOrder, setNoteOrder] = useState(NOTE_ORDERS[0]);
    const [tab, setTab] = useState(SECTIONS[0].value);
    const sections = useRef<Record<string, HTMLElement | null>>({});
    const lockUntil = useRef(0);

    const hasLaunch = filters.launch_id !== "";
    const launch = launches.find((l) => String(l.id) === filters.launch_id);
    // The picker's label when the launch is in its pool; the record's own name when the
    // picker filters have narrowed it out; the id until either has loaded.
    const title = launch?.label
        ?? (main?.name ? `${main.affiliate_name ? `${main.affiliate_name}: ` : ""}${main.name}` : null)
        ?? (hasLaunch ? `Launch ${filters.launch_id}` : "OP Launch Dashboard");
    const params = { launch_id: filters.launch_id, start_date: filters.start_date, end_date: filters.end_date };
    const lifetime = main?.start_date ? { from: main.start_date, to: lastDay(main) } : null;

    const goTab = (value: string) => {
        setTab(value);
        const el = sections.current[value];
        const parent = scrollParent(el);
        if (!el) return;
        lockUntil.current = Date.now() + 700;
        if (parent) {
            const top = el.getBoundingClientRect().top - parent.getBoundingClientRect().top + parent.scrollTop - TAB_OFFSET;
            parent.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
        } else {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    };

    // Scroll spy: the tab follows whichever section has scrolled under the sticky strip.
    useEffect(() => {
        if (!hasLaunch) return;
        const parent = scrollParent(sections.current[SECTIONS[0].value]);
        if (!parent) return;
        const onScroll = () => {
            if (Date.now() < lockUntil.current) return;
            const parentTop = parent.getBoundingClientRect().top;
            let current = SECTIONS[0].value;
            SECTIONS.forEach((s) => {
                const el = sections.current[s.value];
                if (el && el.getBoundingClientRect().top - parentTop <= TAB_OFFSET + 20) current = s.value;
            });
            if (parent.scrollTop + parent.clientHeight >= parent.scrollHeight - 4) current = SECTIONS[SECTIONS.length - 1].value;
            setTab((prev) => (prev === current ? prev : current));
        };
        parent.addEventListener("scroll", onScroll, { passive: true });
        return () => parent.removeEventListener("scroll", onScroll);
    }, [hasLaunch]);

    const ref = (value: string) => (el: HTMLElement | null) => { sections.current[value] = el; };

    return (
        <OpLaunchPage title={hasLaunch ? `${title} | OP Launch Dashboard` : "OP Launch Dashboard"}>
            <Breadcrumb items={[{ label: "Admin" }, { label: "Old Platform" }, { label: "OP Launches", to: `/${Paths.OP_LAUNCHES}` }, { label: hasLaunch ? title : "Pick a launch" }]} />

            <PageHeader title={title}>
                {hasLaunch && (
                    <>
                        <IdChip label="launch" value={filters.launch_id} />
                        <Link to={`/${Paths.OP_LAUNCHES}/${filters.launch_id}`} className={styles.headerLink}>
                            <i className="bi bi-journal-text" aria-hidden="true" />View Launch Details
                        </Link>
                    </>
                )}
                <ReadOnlyTag />
            </PageHeader>

            {hasLaunch && main !== null && <SummaryStrip main={main} />}

            <DashboardFilterBar filters={filters} onApply={apply} lifetime={lifetime}
                launches={launches} launchesLoading={launchesLoading} openRequest={openRequest} />

            {!hasLaunch && (
                <Card>
                    <div className={styles.prompt}>
                        <div className={styles.promptIcon}><i className="bi bi-rocket-takeoff" aria-hidden="true" /></div>
                        <EmptyState title="Pick a launch to see its dashboard" body="Search by name, ID or affiliate in the launch picker above. The other filters narrow its list."
                            action={
                                <>
                                    <Button size="sm" onClick={() => setOpenRequest((n) => n + 1)}>Choose a Launch</Button>
                                    <Link to={`/${Paths.OP_LAUNCHES}`} className={styles.headerLink} style={{ fontSize: "var(--fs-cell)", padding: "0 8px" }}>Browse OP Launches</Link>
                                </>
                            } />
                    </div>
                </Card>
            )}

            {hasLaunch && (
                <>
                    <div className={pageStyles.sticky}>
                        <div className={`${pageStyles.stickyInner} ${styles.tabsRow}`}>
                            <Tabs tabs={SECTIONS} value={tab} onChange={goTab} bare />
                            <span className={styles.rangeCaption}>Showing {formatDay(filters.start_date)} to {formatDay(filters.end_date)}</span>
                        </div>
                    </div>

                    <section ref={ref("overview")} className={styles.section} aria-label="Overview">
                        <SectionLabel>Overview</SectionLabel>
                        <div className={pageStyles.widgets}>
                            <TileWidget tile={TILES.launchDates} params={params} />
                            <TileWidget tile={TILES.served} params={params} />
                            <TileWidget tile={TILES.budgetSummary} params={params} infoTip="The launch's whole life, to its end or the Old Platform's stop day." />
                            <TileWidget tile={TILES.performanceMetrics} params={params} />
                        </div>
                    </section>

                    <section ref={ref("impact")} className={styles.section} aria-label="Impact and Conversions">
                        <SectionLabel>Impact and Conversions</SectionLabel>
                        <div className={pageStyles.widgets}>
                            <TileWidget tile={TILES.impactMetrics} params={params} infoTip="Final values at Old Platform shutdown." style={{ gridColumn: "1 / -1" }} minWidth={900} />
                            <TileWidget tile={TILES.impactTrendline} params={params} />
                            <TileWidget tile={TILES.conversionJourneys} params={params} style={{ gridColumn: "1 / -1" }} minWidth={700} maxHeight={420} />
                        </div>
                    </section>

                    <section ref={ref("spend")} className={styles.section} aria-label="Spend and Impressions">
                        <SectionLabel>Spend and Impressions</SectionLabel>
                        <div className={pageStyles.widgets}>
                            <TileWidget tile={TILES.spendSharePlatform} params={params} />
                            <TileWidget tile={TILES.spendShareVector} params={params} />
                            <TileWidget tile={TILES.impressionsSharePlatform} params={params} />
                            <TileWidget tile={TILES.impressionsShareVector} params={params} />
                            <TileWidget tile={TILES.impressionsShareObjective} params={params} />
                        </div>
                        <div className={pageStyles.widgets}>
                            <TileWidget tile={TILES.spendByPlatform} params={params} />
                            <TileWidget tile={TILES.spendByVector} params={params} />
                            <TileWidget tile={TILES.impressionsByPlatform} params={params} />
                            <TileWidget tile={TILES.impressionsPerConstituent} params={params} />
                        </div>
                    </section>

                    <section ref={ref("notes")} className={styles.section} aria-label="Notes and Key Dates">
                        <SectionLabel>Notes and Key Dates</SectionLabel>
                        <div className={pageStyles.widgets}>
                            <TileWidget tile={TILES.notes} params={params} sort={NOTE_SORT[noteOrder]} maxHeight={340}
                                actions={<SegmentedControl options={NOTE_ORDERS} value={noteOrder} onChange={setNoteOrder} />} />
                            <TileWidget tile={TILES.keyDates} params={params} maxHeight={340} />
                        </div>
                    </section>
                    <div style={{ height: "30vh" }} aria-hidden="true" />
                </>
            )}
        </OpLaunchPage>
    );
}
